import { useEffect, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { AlertCircle, ArrowRight, AtSign, Briefcase, CheckCircle2, Hash, Heart, MapPin, MessageCircle, Sparkles, User, UserPlus } from "lucide-react";
import { COUNTRIES } from "./data";
import { ColorHeader, Field, ModalFrame, ghostBtn, inputCls, primaryBtn, readonlyCls } from "./FormBits";

const YesNo = ({ value, onChange, yesId, noId }) => (
  <div className="flex gap-2 mt-2.5">
    {[["yes", "Yes"], ["no", "No"]].map(([v, label]) => (
      <button
        key={v} type="button"
        data-testid={v === "yes" ? yesId : noId}
        onClick={() => onChange(v)}
        className={`flex-1 rounded-full border px-4 py-2.5 text-sm font-medium transition-colors ${value === v ? (v === "yes" ? "bg-[#2F6B4F] border-[#2F6B4F] text-white" : "bg-[#C86A51] border-[#C86A51] text-white") : "border-[#1A2E26]/15 bg-white hover:bg-[#F4F1EA]"}`}
      >
        {label}
      </button>
    ))}
  </div>
);

const Centered = ({ testId, icon, title, text, textId, children, tone }) => (
  <div className="text-center px-6 sm:px-8 py-8" data-testid={testId}>
    <span className={`mx-auto grid place-items-center w-16 h-16 rounded-full mb-5 ${tone === "amber" ? "bg-[#FFF4E6] text-[#B57E17]" : "bg-[#FFF4EC] text-[#C86A51]"}`}>{icon}</span>
    {title && <h2 className="font-serif text-3xl tracking-tight leading-[1.1] mb-3">{title}</h2>}
    <p data-testid={textId} className={`${title ? "text-sm text-muted-foreground" : "font-serif text-xl text-[#1A2E26]"} leading-relaxed mb-8`}>{text}</p>
    {children}
  </div>
);

export const RegisterModal = ({ open, onClose, onSubmit, form, setForm, content, dialCode, setDialCode, mobileLocal, setMobileLocal, sending, regOpen }) => {
  const country = COUNTRIES.find((c) => c.code === dialCode);
  const numberWarning = (() => {
    if (!mobileLocal) return "";
    const min = country?.len ?? country?.min ?? 6, max = country?.len ?? country?.max ?? 15;
    if (mobileLocal.length < min || mobileLocal.length > max) return `${country?.name || "This country"} numbers have ${min === max ? `${min} digits` : `${min}–${max} digits`} — you typed ${mobileLocal.length}.`;
    if (dialCode === "+91" && !/^[6-9]/.test(mobileLocal)) return "Indian mobile numbers start with 6, 7, 8 or 9.";
    return "";
  })();
  const [stage, setStage] = useState("form"); // form | again | questions | blocked | declined
  const [q1, setQ1] = useState(null);
  const [q2, setQ2] = useState(null);

  useEffect(() => {
    if (open) { setStage("form"); setQ1(null); setQ2(null); }
  }, [open]);

  const handleFormSubmit = async (e) => {
    const data = await onSubmit(e);
    if (data?.repeat_prompt) setStage("again");
    else if (data?.repeat_blocked) setStage("blocked");
  };

  const continueRepeat = async () => {
    if (!q1 || !q2 || sending) return;
    if (q1 === "yes" && q2 === "yes") {
      const data = await onSubmit(null, { repeat_agree: true, repeat_q1: true, repeat_q2: true });
      if (data?.repeat_blocked) setStage("blocked");
    } else {
      setStage("blocked");
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <ModalFrame testId="register-modal" onClose={onClose}>
          {stage === "form" && (
            <>
              <ColorHeader step={2} kicker="Join us" title="Register for the program" sub="Fill in your details — our team will reach out with the next steps." onClose={onClose} closeTestId="register-modal-close" icon={<UserPlus size={20} />} />
              <form onSubmit={handleFormSubmit} data-testid="register-form" className="px-6 sm:px-8 py-6 space-y-4">
                <div className="grid grid-cols-2 gap-3 rounded-2xl border border-dashed border-[#C86A51]/40 bg-[#FFF8F3] p-3">
                  <Field label="Program" icon={<Sparkles size={11} />} tone="amber"><input readOnly data-testid="reg-programname" value={form.program_name} className={readonlyCls} /></Field>
                  <Field label="Batch#" icon={<Hash size={11} />} tone="amber"><input readOnly data-testid="reg-batch" value={content?.batch_no || ""} className={readonlyCls} /></Field>
                </div>
                <Field label="Full name" icon={<User size={11} />}>
                  <input required data-testid="reg-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" className={inputCls} />
                </Field>
                <Field label="WhatsApp Number (with Country Code)" icon={<MessageCircle size={11} />}>
                  <div className="flex gap-2">
                    <select data-testid="reg-country-code" value={dialCode} onChange={(e) => setDialCode(e.target.value)} className={`${inputCls.replace("w-full", "")} w-[7.5rem] shrink-0 pr-7`}>
                      {COUNTRIES.map((c) => <option key={c.code + c.name} value={c.code}>{c.flag} {c.code}</option>)}
                    </select>
                    <input required type="tel" inputMode="numeric" data-testid="reg-mobile" value={mobileLocal} onChange={(e) => setMobileLocal(e.target.value.replace(/\D/g, ""))} maxLength={15} placeholder="WhatsApp number" aria-invalid={!!numberWarning} className={`${inputCls} min-w-0 flex-1 ${numberWarning ? "!border-[#C86A51] !shadow-[0_0_0_3px_rgba(200,106,81,0.15)]" : mobileLocal ? "!border-[#2F6B4F]" : ""}`} />
                  </div>
                  {numberWarning ? <p data-testid="reg-mobile-warning" className="mt-1 text-[11px] text-[#B35A3B] flex items-center gap-1"><AlertCircle size={11} /> {numberWarning}</p> : mobileLocal ? <p data-testid="reg-mobile-ok" className="mt-1 text-[11px] text-[#2F6B4F] flex items-center gap-1"><CheckCircle2 size={11} /> Looks good for {country?.name || "the selected country"}</p> : null}
                </Field>
                <Field label="Email (optional)" icon={<AtSign size={11} />}>
                  <input type="email" data-testid="reg-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@email.com" className={inputCls} />
                </Field>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="Profession" icon={<Briefcase size={11} />}>
                    <input required data-testid="reg-profession" value={form.profession} onChange={(e) => setForm({ ...form, profession: e.target.value })} placeholder="What you do" className={inputCls} />
                  </Field>
                  <Field label="Place" icon={<MapPin size={11} />}>
                    <input required data-testid="reg-place" value={form.place} onChange={(e) => setForm({ ...form, place: e.target.value })} placeholder="City / town" className={inputCls} />
                  </Field>
                </div>
                <button type="submit" disabled={sending || !regOpen} data-testid="reg-submit" className={`${primaryBtn} mt-2`}>
                  {!regOpen ? "Registrations full" : sending ? "Registering…" : "Register now"}
                  {regOpen && <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />}
                </button>
                <p className="text-center text-[11px] text-muted-foreground">Your details are shared only with the PositiveLife team.</p>
              </form>
            </>
          )}

          {stage === "again" && (
            <>
              <ColorHeader step={2} kicker="Welcome back" title="Do the program again?" onClose={onClose} closeTestId="register-modal-close" tone="rose" icon={<Heart size={20} />} />
              <Centered testId="reg-repeat-prompt" icon={<Heart size={28} />} text="You have already done the program. Do you want to do the program again?" >
                <div className="flex gap-3">
                  <button data-testid="reg-repeat-yes" onClick={() => setStage("questions")} className={`${primaryBtn} flex-1`}>Yes</button>
                  <button data-testid="reg-repeat-no" onClick={() => setStage("declined")} className={`${ghostBtn} flex-1`}>No</button>
                </div>
              </Centered>
            </>
          )}

          {stage === "questions" && (
            <>
              <ColorHeader step={2} kicker="One more step" title="Before we continue" onClose={onClose} closeTestId="register-modal-close" tone="rose" icon={<Heart size={20} />} />
              <div data-testid="reg-repeat-questions" className="px-6 sm:px-8 py-6">
                <div className="space-y-4">
                  <div className="rounded-2xl border border-[#1A2E26]/10 bg-white p-4">
                    <p className="text-sm leading-relaxed"><span className="inline-grid place-items-center w-5 h-5 rounded-full bg-[#1A2E26] text-white text-[10px] font-bold mr-2 align-middle">1</span>Are you implementing whatever you learned in PositiveLife?</p>
                    <YesNo value={q1} onChange={setQ1} yesId="reg-q1-yes" noId="reg-q1-no" />
                  </div>
                  <div className="rounded-2xl border border-[#1A2E26]/10 bg-white p-4">
                    <p className="text-sm leading-relaxed"><span className="inline-grid place-items-center w-5 h-5 rounded-full bg-[#1A2E26] text-white text-[10px] font-bold mr-2 align-middle">2</span>Have you gifted PositiveLife to your family members and friends (at least 10 members)?</p>
                    <YesNo value={q2} onChange={setQ2} yesId="reg-q2-yes" noId="reg-q2-no" />
                  </div>
                </div>
                <button data-testid="reg-repeat-continue" disabled={!q1 || !q2 || sending} onClick={continueRepeat} className={`${primaryBtn} mt-6`}>
                  {sending ? "Registering…" : "Complete registration"}
                  <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
                </button>
              </div>
            </>
          )}

          {stage === "blocked" && (
            <>
              <ColorHeader kicker="Thank you" title="Keep the energy flowing" onClose={onClose} closeTestId="register-modal-close" tone="rose" icon={<Heart size={20} />} />
              <Centered testId="reg-repeat-blocked" icon={<Heart size={28} />} textId="reg-blocked-message" text="Please implement for few more weeks and help your family/friends to get this PositiveLife energy. Thank you.">
                <button data-testid="reg-blocked-close" onClick={onClose} className={`${ghostBtn} px-10`}>Close</button>
              </Centered>
            </>
          )}

          {stage === "declined" && (
            <>
              <ColorHeader kicker="Thank you" title="Stay positive" onClose={onClose} closeTestId="register-modal-close" icon={<Heart size={20} />} />
              <Centered testId="reg-repeat-declined" icon={<Heart size={28} />} textId="reg-declined-message" text="No problem at all. Thank you for being a part of the Positive Life family. 🙏">
                <button data-testid="reg-declined-close" onClick={onClose} className={`${ghostBtn} px-10`}>Close</button>
              </Centered>
            </>
          )}
        </ModalFrame>
      )}
    </AnimatePresence>
  );
};
